import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Clock, CaretRight } from "@phosphor-icons/react";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const catLabel = (c) =>
  c === "DEFERRED_INCOME" ? "Deferred Income" : (c || "").charAt(0) + (c || "").slice(1).toLowerCase();

const TYPE_ROUTE = {
  PREPAYMENT: "/prepayments", ACCRUAL: "/accruals",
  DEFERRED_INCOME: "/deferred-income", DEPRECIATION: "/fixed-assets",
};

export default function JournalsDueModal({ open, onClose, onGoToClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/dashboard/journals-due")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [open]);

  const journals = data?.journals || [];

  const openSchedule = (j) => {
    const base = TYPE_ROUTE[j.category] || "/register";
    // The register pages (prepayments/accruals/deferred) open the schedule drawer via ?open=.
    const url = j.schedule_id && base !== "/fixed-assets" ? `${base}?open=${j.schedule_id}` : base;
    onClose();
    navigate(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="journals-due-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl text-navy">Journals due this month</DialogTitle>
          <DialogDescription className="text-navy/70">
            {data?.tenant_name ? `${data.tenant_name} · ` : ""}All release journals dated this month.
            {data && (data.later_count > 0
              ? ` ${data.postable_count} postable now, ${data.later_count} dated later this month (not yet actionable).`
              : " All are postable now.")} Click a row to open its schedule on the register.
          </DialogDescription>
        </DialogHeader>

        {data && (
          <div className="flex flex-wrap items-center gap-2 -mt-1" data-testid="journals-due-policy">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-navy/[0.05] text-navy" data-testid="journals-due-cutoff">
              <Clock size={12} weight="fill" />
              {data.cutoff_fallback
                ? <>Posting cut-off: <b className="ml-1">due today ({fmtDate(data.cutoff)})</b> — no open period, so only journals dated today or earlier post</>
                : <>Posting cut-off: <b className="ml-1">{fmtDate(data.cutoff)}</b> — journals up to and including this date post</>}
            </span>
            {data.approval && (
              <span data-testid="journals-due-approval"
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg ${data.approval.required ? "bg-amber-50 text-amber-700" : "bg-positive/10"}`}
                style={data.approval.required ? undefined : { color: "#00885b" }}>
                {data.approval.required
                  ? `Two-person approval${data.approval.scope === "manual_only" ? " (manual posting only)" : " (all posting)"}`
                  : "Automatic posting — approval not enabled"}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-navy/60" data-testid="journals-due-loading">Loading…</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 overline text-navy/65">
                  <th className="px-3 py-2.5 text-left">Date</th>
                  <th className="px-3 py-2.5 text-left">Type</th>
                  <th className="px-3 py-2.5 text-left">Description</th>
                  <th className="px-3 py-2.5 text-left">Code</th>
                  <th className="px-3 py-2.5 text-left">Status</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {journals.map((j, i) => (
                  <tr key={i} data-testid={`journals-due-row-${i}`} onClick={() => openSchedule(j)}
                    title="Open this schedule on the register"
                    className="group border-b border-slate-100 last:border-0 cursor-pointer hover:bg-orange/[0.04] transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{fmtDate(j.date)}</td>
                    <td className="px-3 py-2.5">{catLabel(j.category)}
                      {j.catch_up && <span className="ml-1.5 text-[0.55rem] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-orange/15 text-orangeink">Catch-up</span>}</td>
                    <td className="px-3 py-2.5 max-w-[200px] truncate" title={j.description}>{j.description}
                      {j.reference ? <div className="text-xs text-navy/65 font-mono">{j.reference}</div> : null}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{j.source_account_code}{j.pl_account_code ? ` → ${j.pl_account_code}` : ""}</td>
                    <td className="px-3 py-2.5">
                      {j.postable ? (
                        <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-positive/10 text-positive" data-testid={`journals-due-status-${i}`}>Postable now</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-navy/55" data-testid={`journals-due-status-${i}`}><Clock size={11} /> Due later</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold tabular">
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        {money(j.amount)}
                        <CaretRight size={13} weight="bold" className="text-slate-300 group-hover:text-orangeink group-hover:translate-x-0.5 transition-all" />
                      </span>
                    </td>
                  </tr>
                ))}
                {journals.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-navy/70" data-testid="journals-due-empty">No journals due this month.</td></tr>
                )}
              </tbody>
              {journals.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-navy/20 font-semibold text-navy">
                    <td className="px-3 py-2.5" colSpan={4}>Total · {data.count} journal(s)</td>
                    <td />
                    <td className="px-3 py-2.5 text-right font-mono tabular" data-testid="journals-due-total">{money(data.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} data-testid="journals-due-close"
            className="px-5 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Close</button>
          <button onClick={onGoToClose} data-testid="journals-due-go-close"
            className="inline-flex items-center gap-1.5 px-6 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors">
            Go to close process <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
